import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { I18nProvider } from './i18n';
import { UnitsProvider } from './context/UnitsContext';
import { ExchangeRatesProvider } from './context/ExchangeRatesContext';
import PreferencesSync from './components/PreferencesSync';
import { Toaster } from './components/ui/sonner';

// Layout (not lazy - needed immediately)
import AppShell from './components/Layout/AppShell';
import { OwnerOnlyRoute } from './components/auth/OwnerOnlyRoute';
import { AdminOnlyRoute } from './components/auth/AdminOnlyRoute';
import { AppHomeRedirect } from './components/auth/AppHomeRedirect';
import { LegacyAppRedirect } from './components/auth/LegacyAppRedirect';
import { LegacyPropertyEditRedirect } from './components/auth/LegacyPropertyEditRedirect';
import { TenantOnlyRoute } from './components/auth/TenantOnlyRoute';
import { RouteAuthSpinner } from './components/auth/RouteAuthSpinner';

// Auth pages (not lazy - first paint)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';

// Lazy-loaded pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'));
const EditPropertyPage = lazy(() => import('./pages/EditPropertyPage'));
const TenantsPage = lazy(() => import('./pages/TenantsPage'));
const FinancesPage = lazy(() => import('./pages/FinancesPage'));
const FinancialDashboardPage = lazy(() => import('./pages/FinancialDashboardPage'));
const AccountingReportsPage = lazy(() => import('./pages/AccountingReportsPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AppPricingPage = lazy(() => import('./pages/AppPricingPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AIInsightsPage = lazy(() => import('./pages/AIInsightsPage'));
const TenantDashboard = lazy(() => import('./pages/TenantDashboard'));
const TenantFinancialsPage = lazy(() => import('./pages/tenant/TenantFinancialsPage'));
const TenantDocumentsPage = lazy(() => import('./pages/tenant/TenantDocumentsPage'));
const TenantInspectionsPage = lazy(() => import('./pages/tenant/TenantInspectionsPage'));
const TenantSetupPage = lazy(() => import('./pages/TenantSetupPage'));

import './App.css';

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-domio-600 border-t-transparent" />
  </div>
);

function App() {
  return (
    <I18nProvider>
      <UnitsProvider>
        <ExchangeRatesProvider>
        <AuthProvider>
          <SubscriptionProvider>
          <PreferencesSync />
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/tenant-setup" element={<Suspense fallback={<PageLoader />}><TenantSetupPage /></Suspense>} />

              {/* Protected routes */}
              <Route path="/app" element={<AppShell />}>
                <Route index element={<AppHomeRedirect />} />
                <Route path="dashboard" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><DashboardPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="properties/:id/edit" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><EditPropertyPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="properties" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><PropertiesPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="tenants" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><TenantsPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="finances" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><FinancesPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="financial" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><FinancialDashboardPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="accounting/reports" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><AccountingReportsPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="maintenance" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><MaintenancePage /></Suspense></OwnerOnlyRoute>} />
                <Route path="meetings" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><MeetingsPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="documents" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><DocumentsPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="ai-insights" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><AIInsightsPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
                <Route path="pricing" element={<OwnerOnlyRoute><Suspense fallback={<PageLoader />}><AppPricingPage /></Suspense></OwnerOnlyRoute>} />
                <Route path="admin" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense></AdminOnlyRoute>} />
                <Route path="tenant" element={<TenantOnlyRoute><Suspense fallback={<RouteAuthSpinner />}><TenantDashboard /></Suspense></TenantOnlyRoute>} />
                <Route path="tenant/financials" element={<TenantOnlyRoute><Suspense fallback={<RouteAuthSpinner />}><TenantFinancialsPage /></Suspense></TenantOnlyRoute>} />
                <Route path="tenant/documents" element={<TenantOnlyRoute><Suspense fallback={<RouteAuthSpinner />}><TenantDocumentsPage /></Suspense></TenantOnlyRoute>} />
                <Route path="tenant/inspections" element={<TenantOnlyRoute><Suspense fallback={<RouteAuthSpinner />}><TenantInspectionsPage /></Suspense></TenantOnlyRoute>} />
              </Route>

              {/* Legacy routes - redirect to /app */}
              <Route path="/dashboard" element={<LegacyAppRedirect ownerTo="/app/dashboard" />} />
              <Route path="/properties" element={<LegacyAppRedirect ownerTo="/app/properties" />} />
              <Route path="/properties/:id/edit" element={<LegacyPropertyEditRedirect />} />
              <Route path="/tenants" element={<LegacyAppRedirect ownerTo="/app/tenants" />} />
              <Route path="/finances" element={<LegacyAppRedirect ownerTo="/app/finances" />} />
              <Route path="/financial" element={<LegacyAppRedirect ownerTo="/app/financial" />} />
              <Route path="/maintenance" element={<LegacyAppRedirect ownerTo="/app/maintenance" />} />
              <Route path="/meetings" element={<LegacyAppRedirect ownerTo="/app/meetings" />} />
              <Route path="/documents" element={<LegacyAppRedirect ownerTo="/app/documents" />} />
              <Route path="/settings" element={<LegacyAppRedirect ownerTo="/app/settings" />} />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          </SubscriptionProvider>
        </AuthProvider>
        </ExchangeRatesProvider>
      </UnitsProvider>
    </I18nProvider>
  );
}

export default App;
